(() => {
  if (window.TtcEventsApi) return;

  const DEFAULT_TIMEZONE = 'Australia/Sydney';
  const requestCache = new Map();
  const AVAILABILITY_LABELS = {
    available: 'Bookings available',
    low: 'Limited places',
    sold_out: 'Sold out',
    closed: 'Bookings closed',
    information_only: 'Information only'
  };

  const TAG_TAXONOMY = {
    tcg: { label: 'Trading card game' },
    'tcg:mtg': { category: 'magic', label: 'Magic: The Gathering' },
    magic: { category: 'magic', label: 'Magic: The Gathering' },
    commander: { category: 'magic', label: 'MTG Commander' },
    draft: { category: 'magic', label: 'MTG Draft' },
    standard: { category: 'magic', label: 'MTG Standard' },
    sealed: { category: 'magic', label: 'MTG Sealed' },
    prerelease: { category: 'magic', label: 'MTG Prerelease' },
    'two-headed-giant': { category: 'magic', label: 'MTG Two-Headed Giant' },
    warhammer: { category: 'warhammer', label: 'Warhammer' },
    '40k': { category: 'warhammer', label: 'Warhammer 40,000' },
    aos: { category: 'warhammer', label: 'Age of Sigmar' },
    'kill-team': { category: 'warhammer', label: 'Kill Team' },
    'blood-bowl': { category: 'warhammer', label: 'Blood Bowl' },
    'tcg:fab': { category: 'flesh-and-blood', label: 'Flesh and Blood' },
    fab: { category: 'flesh-and-blood', label: 'Flesh and Blood' },
    'silver-age': { category: 'flesh-and-blood', label: 'Flesh and Blood · Silver Age' },
    armory: { category: 'flesh-and-blood', label: 'Flesh and Blood Armory' },
    rpg: { category: 'rpg', label: 'Roleplaying game' },
    dnd: { category: 'rpg', label: 'Dungeons & Dragons' },
    private: { category: 'private', label: 'Private room' },
    league: { label: 'League' },
    'league:commons-cup': { category: 'warhammer', label: 'Commons Cup' },
    academy: { label: 'Academy' },
    'academy:mtg': { category: 'magic', label: 'MTG Academy' }
  };

  const first = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

  const normaliseBase = (value) => String(value || '').trim().replace(/\/+$/, '');

  const buildUrl = (base, path = '', parameters = {}) => {
    const normalisedBase = normaliseBase(base);
    if (!normalisedBase) throw new Error('The Abbl events API base URL is empty');

    const normalisedPath = String(path || '').replace(/^\/+/, '');
    const url = new URL(normalisedPath ? `${normalisedBase}/${normalisedPath}` : normalisedBase);
    Object.entries(parameters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
    });
    return url.href;
  };

  const fetchJson = async (url) => {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) throw new Error(`Abbl request returned ${response.status}`);
    return response.json();
  };

  const cachedFetchJson = (url) => {
    if (!requestCache.has(url)) {
      requestCache.set(url, fetchJson(url).catch((error) => {
        requestCache.delete(url);
        throw error;
      }));
    }
    return requestCache.get(url);
  };

  const eventArray = (payload) => {
    if (Array.isArray(payload)) return payload;
    const events = first(payload?.events, payload?.Events, payload?.items, payload?.Items, payload?.data, payload?.Data, []);
    return Array.isArray(events) ? events : [];
  };

  const getUpcoming = async (base, { pageSize = 24, maxPages = 1 } = {}) => {
    const events = [];
    let cursor = '';
    let store = null;

    for (let page = 0; page < maxPages; page += 1) {
      const payload = await cachedFetchJson(buildUrl(base, 'upcoming', {
        limit: Math.max(1, Math.min(100, pageSize)),
        cursor
      }));
      events.push(...eventArray(payload));
      store = payload?.store || store;
      cursor = payload?.pagination?.nextCursor || payload?.nextCursor || '';
      if (!cursor) break;
    }

    return { events, store };
  };

  const getWeekly = async (base) => {
    const payload = await cachedFetchJson(buildUrl(base, 'weekly'));
    return { events: eventArray(payload), store: payload?.store || null };
  };

  const getEvent = async (base, slug) => {
    const payload = await cachedFetchJson(buildUrl(base, encodeURIComponent(String(slug || '').trim())));
    return payload?.event || payload?.booking || payload?.data || payload;
  };

  const dateValue = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const startDate = (event) => dateValue(first(
    event?.eventDate?.from,
    event?.EventDate?.From,
    event?.eventTiming?.start,
    event?.EventTiming?.Start,
    event?.startAt,
    event?.StartAt,
    event?.startsAt,
    event?.StartsAt,
    event?.date,
    event?.Date
  ));

  const endDate = (event) => dateValue(first(
    event?.eventDate?.to,
    event?.EventDate?.To,
    event?.eventTiming?.end,
    event?.EventTiming?.End,
    event?.endAt,
    event?.EndAt,
    event?.endsAt,
    event?.EndsAt
  ));

  const timeZone = (event, store) => first(event?.timezone, store?.timezone, DEFAULT_TIMEZONE);

  const formatDate = (date, options, zone = DEFAULT_TIMEZONE) => {
    if (!date) return '';
    try {
      return new Intl.DateTimeFormat('en-AU', { ...options, timeZone: zone }).format(date);
    } catch (_) {
      return new Intl.DateTimeFormat('en-AU', { ...options, timeZone: DEFAULT_TIMEZONE }).format(date);
    }
  };

  const metadata = (event) => {
    const value = event?.entityData?.data ?? event?.EntityData?.Data;
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  };

  const normaliseTags = (tags) => (Array.isArray(tags) ? tags : [tags])
    .map((tag) => typeof tag === 'string' ? tag : first(tag?.name, tag?.Name, tag?.value, tag?.Value, ''))
    .map((tag) => String(tag || '').trim().toLocaleLowerCase('en-AU'))
    .filter(Boolean);

  const tags = (event) => normaliseTags(first(event?.tags, event?.Tags, event?.eventTags, event?.EventTags, []));

  const matchesTags = (event, requestedTags, mode = 'any') => {
    const required = normaliseTags(requestedTags);
    if (!required.length) return false;
    const available = new Set(tags(event));
    return mode === 'all'
      ? required.every((tag) => available.has(tag))
      : required.some((tag) => available.has(tag));
  };

  const stripHtml = (value) => {
    const temporary = document.createElement('div');
    temporary.innerHTML = String(value || '');
    return temporary.textContent || '';
  };

  const richTextValue = (value) => {
    if (typeof value === 'string') return value;
    return first(value?.html, value?.Html, value?.value, value?.Value, value?.content, value?.Content, '');
  };

  const description = (event) => richTextValue(first(
    event?.shortDescription,
    event?.ShortDescription,
    event?.descriptionHtml,
    event?.DescriptionHtml,
    event?.description,
    event?.Description,
    event?.richText,
    event?.RichText,
    ''
  ));

  const safeUrl = (value, protocols = ['http:', 'https:']) => {
    if (!value) return '';
    try {
      const url = new URL(String(value), window.location.origin);
      return protocols.includes(url.protocol) ? url.href : '';
    } catch (_) {
      return '';
    }
  };

  const imageUrl = (value) => safeUrl(typeof value === 'string' ? value : first(
    value?.url,
    value?.Url,
    value?.src,
    value?.Src,
    value?.imageUrl,
    value?.ImageUrl,
    ''
  ));

  const eventUrl = (event) => safeUrl(first(
    event?.url,
    event?.Url,
    event?.bookingUrl,
    event?.BookingUrl,
    event?.publicUrl,
    event?.PublicUrl,
    event?.pageUrl,
    event?.PageUrl,
    ''
  ));

  const availabilityStatus = (event) => String(first(
    event?.availability?.status,
    event?.Availability?.Status,
    event?.acceptBookings === false || event?.AcceptBookings === false ? 'information_only' : 'available'
  )).toLocaleLowerCase('en-AU');

  const availabilityLabel = (event) => AVAILABILITY_LABELS[availabilityStatus(event)] || 'See event details';

  const remainingPlaces = (event) => first(
    event?.availability?.remaining,
    event?.Availability?.Remaining,
    event?.placesRemaining,
    event?.PlacesRemaining
  );

  const bookingEnabled = (event) => Boolean(event?.acceptBookings ?? event?.AcceptBookings) && Boolean(eventUrl(event));

  const numericRemainingPlaces = (event) => {
    const rawRemaining = remainingPlaces(event);
    if (rawRemaining === undefined || rawRemaining === null || String(rawRemaining).trim() === '') return null;

    const remaining = Number(rawRemaining);
    return Number.isFinite(remaining) ? Math.max(0, Math.floor(remaining)) : null;
  };

  const canBook = (event) => {
    if (!bookingEnabled(event)) return false;

    const status = availabilityStatus(event);
    const remaining = numericRemainingPlaces(event);
    return status !== 'sold_out' && status !== 'closed' && remaining !== 0;
  };

  const priceDisplay = (event) => {
    const rawAmount = first(event?.price?.amount, event?.Price?.Amount);
    if (rawAmount === undefined || rawAmount === null || String(rawAmount).trim() === '') return '';

    const amount = Number(rawAmount);
    if (!Number.isFinite(amount)) return '';
    if (amount === 0) return 'FREE';

    const display = first(event?.price?.display, event?.Price?.Display, event?.priceDisplay, event?.PriceDisplay);
    if (display !== undefined && display !== null && String(display).trim() !== '') return String(display);

    const currency = first(event?.price?.currency, event?.Price?.Currency, 'AUD');
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: String(currency),
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Kept as an API alias for existing section scripts. Missing prices remain blank;
  // only an explicit numeric zero is displayed as FREE.
  const priceOrFree = (event) => priceDisplay(event);

  const category = (event) => {
    const eventTags = tags(event);
    const taxonomyMatch = eventTags
      .map((tag) => TAG_TAXONOMY[tag])
      .find((entry) => entry?.category);
    return taxonomyMatch?.category || 'all';
  };

  const categoryLabel = (value) => ({
    magic: 'Magic: The Gathering',
    warhammer: 'Warhammer',
    'flesh-and-blood': 'Flesh and Blood',
    rpg: 'D&D and RPGs',
    private: 'Private room'
  })[value] || 'Tabletop gaming';

  const eventLabel = (event) => {
    const eventTags = new Set(tags(event));
    if (eventTags.has('blood-bowl')) return 'Blood Bowl';
    if (eventTags.has('40k')) return 'Warhammer 40,000';
    if (eventTags.has('aos')) return 'Warhammer Age of Sigmar';
    if (eventTags.has('kill-team')) return 'Warhammer Kill Team';
    if (eventTags.has('tcg:mtg') || eventTags.has('magic') || eventTags.has('academy:mtg')) return 'Magic: The Gathering';
    if (eventTags.has('tcg:fab') || eventTags.has('fab')) return 'Flesh and Blood';
    if (eventTags.has('dnd')) return 'Dungeons & Dragons';
    if (eventTags.has('private')) return 'Private Gaming Room';
    return categoryLabel(category(event));
  };

  const eventDefinitionKey = (event) => {
    const id = String(first(event?.id, event?.Id, ''));
    const match = id.match(/^([a-z]+-\d+)-/i);
    return match?.[1] || '';
  };

  const recurringSignature = (event, store = null) => {
    const start = startDate(event);
    if (!start) return '';
    const zone = timeZone(event, store);
    const name = String(first(event?.name, event?.Name, event?.title, event?.Title, '')).trim().toLocaleLowerCase('en-AU');
    const weekday = formatDate(start, { weekday: 'long' }, zone);
    const time = formatDate(start, { hour: '2-digit', minute: '2-digit', hour12: false }, zone);
    return `${name}::${weekday}::${time}`;
  };

  const isWeeklyEvent = (event, weeklyEvents = [], store = null) => {
    const eventTags = new Set(tags(event));
    const eventMetadata = metadata(event);
    const recurrence = String(first(eventMetadata.recurrence, eventMetadata.repeat, '')).toLocaleLowerCase('en-AU');
    if (eventTags.has('weekly') || eventTags.has('recurring') || recurrence === 'weekly') return true;

    const definitionKey = eventDefinitionKey(event);
    const signature = recurringSignature(event, store);
    return weeklyEvents.some((weeklyEvent) => (
      (definitionKey && eventDefinitionKey(weeklyEvent) === definitionKey)
      || (signature && recurringSignature(weeklyEvent, store) === signature)
    ));
  };

  const capacityDisplay = (event, { weeklyEvents = [], store = null } = {}) => {
    if (isWeeklyEvent(event, weeklyEvents, store) || !bookingEnabled(event)) return null;

    const status = availabilityStatus(event);
    if (status === 'closed' || status === 'information_only') return null;

    const remaining = numericRemainingPlaces(event);
    if (remaining === null && status !== 'sold_out') return null;
    if (status === 'sold_out' || remaining === 0) {
      return { state: 'sold-out', label: 'Sold out', remaining: 0 };
    }

    const singular = remaining === 1;
    return {
      state: remaining < 5 ? 'urgent' : remaining <= 10 ? 'low' : 'available',
      label: `${remaining} ${singular ? 'spot' : 'spots'} left${remaining < 5 ? '!' : ''}`,
      remaining
    };
  };

  const occurrenceLabel = (event, { weeklyEvents = [], store = null } = {}) => {
    if (!isWeeklyEvent(event, weeklyEvents, store)) return 'Limited Event';
    const start = startDate(event);
    const weekday = formatDate(start, { weekday: 'long' }, timeZone(event, store));
    return weekday ? `Every ${weekday}` : 'Weekly Event';
  };

  const eventPageUrl = (event) => {
    const eventMetadata = metadata(event);
    const explicitUrl = safeUrl(first(
      eventMetadata.shopifyPageUrl,
      eventMetadata.eventPageUrl,
      eventMetadata.pageUrl,
      event?.shopifyPageUrl,
      event?.ShopifyPageUrl
    ));
    if (explicitUrl) return explicitUrl;

    const eventTags = new Set(tags(event));
    const name = String(first(event?.name, event?.Name, event?.title, event?.Title, '')).toLocaleLowerCase('en-AU');
    if (eventTags.has('prerelease')) return safeUrl('/pages/mtg-prerelease-illawarra');
    if (eventTags.has('academy') || eventTags.has('academy:mtg')) return safeUrl('/pages/mtg-academy');
    if (eventTags.has('league:commons-cup') || (eventTags.has('blood-bowl') && name.includes('league'))) {
      return safeUrl('/pages/commons-cup-blood-bowl-league');
    }
    if (eventTags.has('private')) return safeUrl('/pages/private-gaming-room');
    if (eventTags.has('commander') || name.includes('commander')) return safeUrl('/pages/mtg-commander-illawarra');
    if (eventTags.has('standard') || name.includes('standard')) return safeUrl('/pages/mtg-standard-illawarra');
    if (eventTags.has('draft') || name.includes('draft')) return safeUrl('/pages/mtg-booster-draft-illawarra');
    if (eventTags.has('tcg:mtg') || eventTags.has('magic')) return safeUrl('/pages/magic-the-gathering');
    if (eventTags.has('tcg:fab') || eventTags.has('fab')) return safeUrl('/pages/flesh-and-blood');
    if (eventTags.has('warhammer') || eventTags.has('40k') || eventTags.has('aos') || eventTags.has('kill-team') || eventTags.has('blood-bowl')) {
      return safeUrl('/pages/warhammer');
    }
    return eventUrl(event);
  };

  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;'
  })[character]);

  const eventArtStyle = (event, name) => {
    const seed = eventDefinitionKey(event) || `${name}|${tags(event).join('|')}`;
    const hash = [...seed].reduce((total, character) => ((total * 31) + character.charCodeAt(0)) >>> 0, 0);
    return String((hash % 4) + 1);
  };

  const createEventCard = (event, { store = null, label = '', weeklyEvents = [] } = {}) => {
    const name = first(event?.name, event?.Name, event?.title, event?.Title, 'Tabletop Commons event');
    const eventDescription = description(event);
    const start = startDate(event);
    const end = endDate(event);
    const zone = timeZone(event, store);
    const eventCategory = category(event);
    const informationUrl = eventPageUrl(event);
    const bookingUrl = canBook(event) ? eventUrl(event) : '';
    const thumbnailSource = first(
      event?.thumbnail,
      event?.Thumbnail,
      event?.thumbnailUrl,
      event?.ThumbnailUrl
    );
    const thumbnail = imageUrl(thumbnailSource);
    const thumbnailAlt = typeof thumbnailSource === 'object'
      ? first(thumbnailSource?.alt, thumbnailSource?.Alt, '')
      : '';
    const thumbnailWidth = Number(first(thumbnailSource?.width, thumbnailSource?.Width));
    const thumbnailHeight = Number(first(thumbnailSource?.height, thumbnailSource?.Height));
    const thumbnailDimensions = Number.isFinite(thumbnailWidth) && thumbnailWidth > 0 && Number.isFinite(thumbnailHeight) && thumbnailHeight > 0
      ? ` width="${thumbnailWidth}" height="${thumbnailHeight}"`
      : '';
    const price = priceOrFree(event);
    const status = occurrenceLabel(event, { weeklyEvents, store });
    const capacity = capacityDisplay(event, { weeklyEvents, store });
    const dateLabel = start ? formatDate(start, { weekday: 'short', day: 'numeric', month: 'short' }, zone) : 'Date to be confirmed';
    const timeLabel = formatDate(start, { hour: 'numeric', minute: '2-digit' }, zone);
    const endLabel = end && start && end.getTime() > start.getTime()
      ? `–${formatDate(end, { hour: 'numeric', minute: '2-digit' }, zone)}`
      : '';

    const article = document.createElement('article');
    article.className = 'ttc-events__card';
    article.dataset.eventCard = '';
    article.dataset.category = eventCategory;
    article.dataset.artStyle = eventArtStyle(event, name);
    if (thumbnail) article.classList.add('ttc-events__card--has-image');
    article.innerHTML = `
      <div class="ttc-events__card-top">
        ${thumbnail ? `<div class="ttc-events__card-thumb"><img src="${escapeHtml(thumbnail)}" alt="${escapeHtml(thumbnailAlt)}"${thumbnailDimensions} loading="lazy" decoding="async"></div>` : ''}
        <span class="ttc-events__tag">${escapeHtml(label || eventLabel(event))}</span>
        <span class="ttc-events__date">${escapeHtml(dateLabel)}</span>
      </div>
      <div class="ttc-events__card-body">
        <h3 class="ttc-events__card-title">${escapeHtml(name)}</h3>
        ${timeLabel ? `<p class="ttc-events__card-meta">${escapeHtml(`${timeLabel}${endLabel}`)}</p>` : ''}
        ${eventDescription ? `<p class="ttc-events__card-copy">${escapeHtml(stripHtml(eventDescription).slice(0, 190))}</p>` : ''}
        <div class="ttc-events__card-footer">
          ${price ? `<span class="ttc-events__price">${escapeHtml(price)}</span>` : ''}
          <span class="ttc-events__status">${escapeHtml(status)}</span>
        </div>
        ${capacity ? `<div class="ttc-events__capacity ttc-events__capacity--${escapeHtml(capacity.state)}" aria-label="Booking availability: ${escapeHtml(capacity.label)}">
          <span class="ttc-events__capacity-dot" aria-hidden="true"></span>
          <strong>${escapeHtml(capacity.label)}</strong>
        </div>` : ''}
        ${(informationUrl || bookingUrl) ? `<div class="ttc-events__card-actions">
          ${informationUrl ? `<a class="ttc-events__card-info" href="${escapeHtml(informationUrl)}">View event information <span aria-hidden="true">→</span></a>` : ''}
          ${bookingUrl ? `<a class="ttc-events__button ttc-events__button--small" href="${escapeHtml(bookingUrl)}">Book now <span aria-hidden="true">→</span></a>` : ''}
        </div>` : ''}
      </div>`;
    return article;
  };

  window.TtcEventsApi = {
    DEFAULT_TIMEZONE,
    availabilityLabel,
    availabilityStatus,
    buildUrl,
    bookingEnabled,
    canBook,
    capacityDisplay,
    category,
    categoryLabel,
    createEventCard,
    description,
    endDate,
    eventArray,
    eventDefinitionKey,
    eventPageUrl,
    eventUrl,
    fetchJson,
    first,
    formatDate,
    getEvent,
    getUpcoming,
    getWeekly,
    imageUrl,
    isWeeklyEvent,
    eventLabel,
    escapeHtml,
    TAG_TAXONOMY,
    matchesTags,
    metadata,
    normaliseBase,
    normaliseTags,
    occurrenceLabel,
    priceDisplay,
    priceOrFree,
    remainingPlaces,
    richTextValue,
    safeUrl,
    startDate,
    stripHtml,
    tags,
    timeZone
  };
})();
